import { CharacterCount, Textarea } from '@h2hspark/ui';
import { Check, Lock } from 'lucide-react';
import * as React from 'react';

import { useSaveConnectionNote } from './api';
import {
  NOTE_LABEL,
  NOTE_MAX_LENGTH,
  NOTE_PLACEHOLDER,
  NOTE_PRIVACY_QUALIFIER,
  NOTE_SAVED_NOTICE,
  NOTE_SAVE_FAILED,
  NOTE_SAVE_LABEL,
  NOTE_SAVING_LABEL,
} from './constants';
import type { ConnectionDetail } from './types';

/**
 * The private note (`MEM-033` AC-3, AC-4).
 *
 * ## Three specified strings, rendered verbatim
 *
 * `MEM-CON-010` fixes the label *"Your notes"*, the qualifier *"Private to you"* and the
 * placeholder word for word. All three live in `constants.ts` and are asserted there, because
 * `apps/web` has no DOM renderer and a string inside JSX is a string with no test.
 *
 * **The qualifier is not decoration.** It is the only place a member is told what the two-row
 * model guarantees them, and `MEM-033` AC-4 is the strongest privacy promise in the product: this
 * note reaches its author and nobody else — not the other member, not an organizer, not an
 * administrator, at any permission level. The lock icon says it again without words.
 *
 * ## Explicit saving
 *
 * One Save button, and nothing writes without it — the house rule since the organizer wizard's
 * autosave was removed on 2026-08-19, and the shape `matching-preferences-form.tsx` set for a
 * member-facing form. There is no timer, no blur handler and no effect that writes.
 *
 * That matters more here than on a preferences form. An autosaving note is a note being
 * transmitted while somebody is still deciding what they think of a person they just met, and
 * *"Private to you"* is easier to believe when the member decides when it leaves the screen.
 *
 * The prototype puts *Save note* in a fixed footer bar; here it sits under the field, because the
 * member app's screens scroll inside a shell that already has a fixed tab bar and a second one
 * would leave the field showing three lines on a phone.
 */
export function PrivateNotePanel({
  connection,
}: {
  readonly connection: ConnectionDetail;
}): React.ReactElement {
  const save = useSaveConnectionNote(connection.connectionRef);
  const [draft, setDraft] = React.useState(connection.note ?? '');
  const [saved, setSaved] = React.useState(false);
  /** What the server last confirmed. The baseline `dirty` is measured against. */
  const baseline = React.useRef(connection.note ?? '');

  const dirty = draft !== baseline.current;
  const tooLong = draft.length > NOTE_MAX_LENGTH;

  return (
    <div className="space-y-2">
      {/* `Web App.dc.html` §2.11's note card heads itself with an 18px `lock` in
          `--brand-primary` beside an `<h2>` at `var(--text-lg)`, over a muted sentence. The
          heading and the qualifier are `MEM-CON-010`'s own words — the prototype writes *"Your
          private note"* and *"Only you can see this…"*, and the specification fixes these two
          verbatim, so the shape is the prototype's and the strings are the specification's. */}
      <div className="flex items-center gap-2.5">
        <Lock className="h-[18px] w-[18px] shrink-0 text-primary" aria-hidden />
        <h2 className="text-lg">{NOTE_LABEL}</h2>
      </div>
      <p className="text-sm text-muted-foreground">{NOTE_PRIVACY_QUALIFIER}</p>

      <Textarea
        id="connection-note"
        value={draft}
        placeholder={NOTE_PLACEHOLDER}
        aria-label={`${NOTE_LABEL} — ${NOTE_PRIVACY_QUALIFIER}`}
        className="min-h-[120px] resize-none"
        onChange={(event) => {
          setSaved(false);
          setDraft(event.target.value);
        }}
      />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!dirty || tooLong || save.isPending}
            onClick={() => {
              save.mutate(
                { note: draft },
                {
                  onSuccess: (data) => {
                    // Rebased on what the server stored rather than on what was sent: the API
                    // trims, and a draft that stayed "dirty" against an untrimmed baseline would
                    // leave Save enabled after a successful save.
                    baseline.current = data.connection.note ?? '';
                    setDraft(data.connection.note ?? '');
                    setSaved(true);
                  },
                },
              );
            }}
            className="inline-flex items-center justify-center gap-1.5 rounded-pill bg-brand px-5 py-2.5 font-brand text-sm font-bold text-white disabled:opacity-50"
          >
            <Check className="h-4 w-4" aria-hidden />
            {save.isPending ? NOTE_SAVING_LABEL : NOTE_SAVE_LABEL}
          </button>
          {saved && !dirty ? (
            <span role="status" className="text-xs font-semibold text-text-subtle">
              {NOTE_SAVED_NOTICE}
            </span>
          ) : null}
          {save.isError ? (
            <span role="alert" className="text-xs font-semibold text-destructive">
              {NOTE_SAVE_FAILED}
            </span>
          ) : null}
        </div>
        <CharacterCount used={draft.length} max={NOTE_MAX_LENGTH} />
      </div>
    </div>
  );
}
